import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/genres";

const H = "h-16 md:h-[68px]";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `px-3 lg:px-4 py-2 text-sm lg:text-[15px] rounded-md transition-colors cursor-pointer whitespace-nowrap ${
    isActive
      ? "text-background-50 font-semibold"
      : "text-background-200 hover:text-background-50"
  }`;

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [genreOpen, setGenreOpen] = useState(false);
  const [mobileGenreOpen, setMobileGenreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const genreRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (genreRef.current && !genreRef.current.contains(e.target as Node)) {
        setGenreOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    setSearchOpen(false);
    setMobileOpen(false);
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-primary-950">
      <div className={`flex ${H} items-center justify-between px-4 md:px-6`}>
        {/* Brand */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="w-9 h-9 rounded-md bg-accent-500 flex items-center justify-center">
              <i className="ri-film-line text-xl text-background-50"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-background-50 font-heading font-black text-lg md:text-xl tracking-wide">
                最新电影
              </span>
              <span className="hidden sm:block text-[10px] tracking-[0.22em] text-background-300 uppercase">
                Latest Movies
              </span>
            </span>
          </Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/" end className={navLinkClass}>
            首页
          </NavLink>
          <div className="relative" ref={genreRef}>
            <button
              type="button"
              onClick={() => setGenreOpen((v) => !v)}
              className="flex items-center gap-1 px-3 lg:px-4 py-2 text-sm lg:text-[15px] rounded-md text-background-200 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              分类
              <i
                className={`ri-arrow-down-s-line text-base transition-transform ${
                  genreOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
            {genreOpen && (
              <div className="absolute left-0 top-full mt-2 w-[520px] rounded-lg bg-background-50 p-4 shadow-lg border border-background-200 animate-fade-in-up">
                <p className="text-xs font-medium text-foreground-500 mb-3">按类型浏览电影</p>
                <div className="grid grid-cols-3 gap-1">
                  {genres.map((g) => (
                    <Link
                      key={g.slug}
                      to={`/category/${g.slug}`}
                      onClick={() => setGenreOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <i className={`${g.icon} text-lg text-accent-500`}></i>
                      {g.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/category/action" className={navLinkClass}>
            全部影片
          </NavLink>
          <NavLink to="/cinema" className={navLinkClass}>
            影院动态
          </NavLink>
          <NavLink to="/about" className={navLinkClass}>
            关于
          </NavLink>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center">
            {searchOpen ? (
              <form onSubmit={submitSearch} className="flex items-center bg-background-100 rounded-md pl-3">
                <i className="ri-search-line text-foreground-500 text-base"></i>
                <input
                  autoFocus
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索电影、导演…"
                  className="w-40 lg:w-52 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 px-2 py-2 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-3 py-2 text-sm font-medium text-background-50 bg-accent-500 rounded-md cursor-pointer whitespace-nowrap"
                >
                  搜索
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-background-200 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line text-lg"></i>
                搜索
              </button>
            )}
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-background-100 hover:bg-primary-900 transition-colors cursor-pointer"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-primary-900 bg-primary-950 px-4 pb-5 pt-3">
          <form onSubmit={submitSearch} className="flex items-center bg-background-100 rounded-md pl-3 mb-3">
            <i className="ri-search-line text-foreground-500 text-base"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、导演…"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 px-2 py-2.5 focus:outline-none"
            />
            <button
              type="submit"
              className="px-4 py-2.5 text-sm font-medium text-background-50 bg-accent-500 rounded-md cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <div className="flex flex-col">
            {[
              { to: "/", label: "首页" },
              { to: "/category/action", label: "全部影片" },
              { to: "/cinema", label: "影院动态" },
              { to: "/about", label: "关于我们" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="py-3 px-2 text-[15px] text-background-100 border-b border-primary-900 cursor-pointer"
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => setMobileGenreOpen((v) => !v)}
              className="flex items-center justify-between py-3 px-2 text-[15px] text-background-100 border-b border-primary-900 cursor-pointer"
            >
              分类浏览
              <i className={`${mobileGenreOpen ? "ri-subtract-line" : "ri-add-line"} text-lg`}></i>
            </button>
            {mobileGenreOpen && (
              <div className="grid grid-cols-3 gap-1 py-3">
                {genres.map((g) => (
                  <Link
                    key={g.slug}
                    to={`/category/${g.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex flex-col items-center gap-1 py-2 rounded-md bg-primary-900 text-background-100 text-xs cursor-pointer"
                  >
                    <i className={`${g.icon} text-lg text-accent-400`}></i>
                    {g.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}